/**
 * DTOs du reporting fédéré — guide v1.2 §7.
 *
 * Les exemples PDF (PRINCIPAL/FEE/NET) sont un artefact de générateur :
 * on aligne sur §7.1 (résumés fusionnés, service injoignable dans
 * `errors`) et on parse de façon défensive.
 */

export const REPORTING_SERVICES = [
  "kyc",
  "wallet",
  "ledger",
  "fee-engine",
  "merchant",
] as const;

export type ReportingServiceName = (typeof REPORTING_SERVICES)[number];

/** Mesures §7.4. */
export const REPORTING_METRIC_KEYS = [
  "transaction.count",
  "transaction.fee",
  "transaction.agent_commission",
  "transaction.failure",
  "wallet.created",
  "wallet.active",
  "kyc.party_created",
  "kyc.case_opened",
  "kyc.case_submitted",
  "kyc.case_reviewed",
  "kyc.review_delay_minutes",
  "kyc.sanctions_screening",
  "ledger.journal_entry",
  "ledger.posted_amount",
  "merchant.sale_gross",
  "merchant.sale_fee",
  "merchant.sale_net",
  "merchant.active_merchants",
  "merchant.created",
  "fee.computed",
  "fee.base_amount",
  "fee.promotion_discount",
] as const;

export type ReportingMetricKey = (typeof REPORTING_METRIC_KEYS)[number];

/** Jauges critiques §7.5. */
export const CRITICAL_GAUGE_NAMES = [
  "flashpay_wallet_stuck_awaiting_ledger",
  "flashpay_ledger_balance_gap_minor",
  "flashpay_kyc_pending_review",
  "flashpay_merchant_active_without_settlement_wallet",
  "flashpay_fee_subscriptions_past_due",
] as const;

export type CriticalGaugeName = (typeof CRITICAL_GAUGE_NAMES)[number];

export const MAX_REPORTING_RANGE_DAYS = 400;
export const DEFAULT_REPORTING_RANGE_DAYS = 30;

export interface ReportingRangeParams {
  from?: string;
  to?: string;
}

export interface ReportingSeriesParams extends ReportingRangeParams {
  metricKey?: string;
  currency?: string;
}

/** Point de série journalière (agrégat `count` + `sumMinor`). */
export interface ReportingSeriesPointDto {
  date: string;
  metricKey: string;
  dimension1?: string;
  dimension2?: string;
  currency?: string;
  count?: number;
  sumMinor?: number;
}

export interface ReportingTotalDto {
  metricKey: string;
  currency?: string;
  count?: number;
  sumMinor?: number;
}

export interface ReportingGaugeDto {
  name: string;
  help?: string;
  value: number;
  labels?: Record<string, unknown>;
}

export interface ReportingSummaryDto {
  service?: string;
  from?: string;
  to?: string;
  totals?: ReportingTotalDto[];
  gauges?: ReportingGaugeDto[];
  generatedAt?: string;
}

export interface ReportingServiceError {
  service: string;
  message: string;
}

/** Vue consolidée gateway — `services` = résumés fusionnés, `errors` = injoignables. */
export interface ReportingOverviewDto {
  from?: string;
  to?: string;
  services: Record<string, ReportingSummaryDto>;
  errors: ReportingServiceError[];
  generatedAt?: string;
}
